// Active positions (SM-712; Trading group SM-829): the positions of the
// node's users in the selected currency, read from settlement-service by
// the positions' stamped broker_id (plan D67). Scope opens on the whole
// subtree — every permitted descendant too (SM-862; a subtree over 500
// nodes answers 422 BROKER_000111 — switch to Serviced directly or narrow
// to a descendant); ?scope=direct = accounts this node services. The User
// picker narrows the list to one account by reading that account's own
// route (…/accounts/{id}/positions — the account travels in the path).
//
// Status: "Active" — OPEN and REDEEMABLE, sent as one comma list — is the
// default; All is ?status=ALL (a URL value only, never sent, so it
// survives a reload); or one status. Refreshes every 30 s.
//
//   GET /api/broker/{id}/positions?scope=&currency=&status=&offset=&limit=
//   GET /api/broker/{id}/accounts/{aid}/positions?currency=&status=&offset=&limit=   (a picked user)
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery, type QueryKey } from "@tanstack/react-query";
import { capabilityGuard, Guarded } from "@/components/layout/guard";
import { PageHeader } from "@/components/layout/PageHeader";
import { useScope } from "@/components/layout/ScopeContext";
import { SectionCard } from "@/components/ui/Card";
import { EmptyPage, Pager } from "@/components/ui/Common";
import { QueryBlock } from "@/components/ui/States";
import { Select } from "@/components/ui/Field";
import { PositionsTable } from "@/components/PositionsTable";
import { TRADING_PAGE_LIMIT, TradingFilters, validateTradingSearch, type TradingSearch } from "@/components/TradingFilters";
import { fetchAccountPositions, fetchPositions, keys } from "@/lib/queries";
import {
  ACTIVE_POLL_MS,
  POSITION_STATUSES,
  POSITION_STATUS_ALL,
  parsePositionStatus,
  positionStatusParam,
  scopeFromSearch,
} from "@/lib/trading";
import type { AccountScope, PositionListResponse } from "@/lib/types";
import { useI18n } from "@/i18n/I18nProvider";

interface PositionsSearch extends TradingSearch {
  /** Absent = active (OPEN,REDEEMABLE); "ALL" = every status; else one status. */
  status?: string;
}

export const Route = createFileRoute("/_app/positions")({
  validateSearch: (s: Record<string, unknown>): PositionsSearch => ({
    ...validateTradingSearch(s),
    status: parsePositionStatus(s.status),
  }),
  beforeLoad: capabilityGuard("VIEW"),
  component: PositionsPage,
});

function PositionsPage() {
  const { t } = useI18n();
  const { guard } = Route.useRouteContext();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const { nodeId, currency } = useScope();
  const scope: AccountScope = scopeFromSearch(search.scope);
  const account = search.account;
  const page = { currency, status: positionStatusParam(search.status), offset: search.offset ?? 0, limit: TRADING_PAGE_LIMIT };
  const nodeParams = { scope, ...page };
  const probe: { queryKey: QueryKey; queryFn: () => Promise<PositionListResponse> } = account
    ? { queryKey: keys.accountPositions(nodeId, account, page), queryFn: () => fetchAccountPositions(nodeId, account, page) }
    : { queryKey: keys.positions(nodeId, nodeParams), queryFn: () => fetchPositions(nodeId, nodeParams) };
  const q = useQuery({ ...probe, enabled: guard.kind === "allow", refetchInterval: ACTIVE_POLL_MS });
  const set = (patch: Partial<PositionsSearch>) => void navigate({ search: (prev) => ({ ...prev, offset: undefined, ...patch }) });
  const onOffset = (o: number) => set({ offset: o || undefined });
  const hint = t(account ? "trading.emptyHintAccount" : "trading.emptyHintNode");

  return (
    <Guarded guard={guard} probe={probe}>
      <PageHeader
        title={t("positions.title")}
        subtitle={t("positions.subtitle")}
        coverage={scope === "subtree" ? "subtree" : "node"}
        money
      />
      <SectionCard title={t("positions.list")}>
        <TradingFilters scope={scope} account={account} onScope={(v) => set({ scope: v })} onAccount={(a) => set({ account: a })}>
          <div className="w-56">
            <Select
              label={t("common.status")}
              value={search.status ?? ""}
              onChange={(e) => set({ status: e.target.value || undefined })}
              options={[
                { value: "", label: t("positions.statusActive") },
                { value: POSITION_STATUS_ALL, label: t("common.all") },
                ...POSITION_STATUSES.map((s) => ({ value: s, label: t(`positionStatus.${s}`) })),
              ]}
            />
          </div>
        </TradingFilters>
        <QueryBlock
          query={q}
          empty={(d) =>
            (d.positions ?? []).length === 0 ? (
              <EmptyPage
                title={t(search.status === undefined ? "positions.emptyActive" : "positions.empty")}
                hint={hint}
                page={d.page}
                onOffset={onOffset}
              />
            ) : null
          }
        >
          {(d) => (
            <>
              <PositionsTable positions={d.positions ?? []} />
              <Pager page={d.page} onOffset={onOffset} />
            </>
          )}
        </QueryBlock>
      </SectionCard>
    </Guarded>
  );
}
